import axios from 'axios'
import axiosInstance from '../../axios/axiosInstance'
import { REACT_APP_AUTH_API_DEV } from '../../utils/config'
import type { ReferralStatus } from '../../utils/referral'

/** Give-get referral API (dev-backend/src/core/referral). Both calls fail soft to "no referral". */
export async function fetchReferralStatus(): Promise<ReferralStatus | null> {
  try {
    const res = await axiosInstance.get(`${REACT_APP_AUTH_API_DEV}referral/status`)
    return res?.data ?? null
  } catch {
    return null
  }
}

// One status request per login, shared by the header link, the invite card and /refer. Failures
// are not kept, so the next caller asks again.
let statusCache: { token: string | null; promise: Promise<ReferralStatus | null> } | null = null

/** fetchReferralStatus, reused for as long as the same person is logged in. */
export function getReferralStatus(): Promise<ReferralStatus | null> {
  const token = localStorage.getItem('authToken')
  if (statusCache && statusCache.token === token) return statusCache.promise
  const promise = fetchReferralStatus().then((status) => {
    if (!status && statusCache?.promise === promise) statusCache = null
    return status
  })
  statusCache = { token, promise }
  return promise
}

/** Forget the cached status, e.g. after a plan change, so the next caller asks the server again. */
export function invalidateReferralStatus(): void {
  statusCache = null
}

export async function checkReferralCode(code: string): Promise<boolean> {
  try {
    const res = await axios.get(`${REACT_APP_AUTH_API_DEV}referral/code/${encodeURIComponent(code)}`)
    return !!res?.data?.valid
  } catch {
    return false
  }
}

export interface ReferralInvites {
  clicks: number
  signedUp: number
  onTrial: number
  subscribed: number
  friends: { name: string; joinedAt: string | null; status: 'signed_up' | 'on_trial' | 'subscribed'; rewarded: boolean }[]
}

/** The referrer's invite tracker on /refer. */
export async function fetchReferralInvites(): Promise<ReferralInvites | null> {
  try {
    const res = await axiosInstance.get(`${REACT_APP_AUTH_API_DEV}referral/invites`)
    return res?.data ?? null
  } catch {
    return null
  }
}
